import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, functions } from '../firebase';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';

// Reusable passwordless sign-in form, extracted from SignInScreen so the same flow
// backs both the listening app's sign-in screen and the inline rent sign-in on the
// audiobook access card. It renders ONLY the form controls and messages — no theme
// wrapper, no wordmark, no marketing heading — so it inherits whatever palette
// surrounds it: gold-on-black inside .listening-theme, the lighter card theme inside
// PurchaseGate.
//
// Behaviour is identical to the original: request a 6-digit code by email, verify it,
// then exchange it for a real Firebase session via signInWithCustomToken. On success
// it simply returns and relies on the app-wide auth-state change to advance the UI.
//
// emailIntro is an optional line shown above the email field. The listening screen
// passes its "Your audiobooks, wherever you are." tagline; other callers omit it and
// supply their own heading around the form. No props are required.
export default function SignInForm({ emailIntro = null }) {
  // 'email' = collect the address; 'code' = collect the 6-digit code.
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // STATE 1 button: request a code, then advance to the code-entry state.
  async function handleEmailSubmit() {
    const value = email.trim().toLowerCase();
    if (!value) return;
    setSending(true);
    setError('');
    setNotice('');
    try {
      const fn = httpsCallable(functions, 'sendSignInCode');
      await fn({ email: value });
      setStep('code');
    } catch (err) {
      console.error('sendSignInCode failed:', err);
      setError("We couldn't send your code. Please check the email and try again.");
    } finally {
      setSending(false);
    }
  }

  // STATE 2 button: verify the code, then sign in with the returned token.
  async function handleSignIn() {
    const value = email.trim().toLowerCase();
    const entered = code.trim();
    if (!entered) return;
    setVerifying(true);
    setError('');
    setNotice('');
    try {
      const fn = httpsCallable(functions, 'verifySignInCode');
      const res = await fn({ email: value, code: entered });
      if (res.data?.success && res.data.token) {
        // Establish the real Firebase session. The auth-state change is what
        // advances the UI — nothing more to do here.
        await signInWithCustomToken(auth, res.data.token);
        return;
      }
      const reason = res.data?.reason;
      if (reason === 'expired') {
        setError('That code has expired, send a new one.');
      } else if (reason === 'too_many') {
        setError('Too many attempts. Please send a new code.');
      } else {
        setError("That code wasn't right, try again.");
      }
    } catch (err) {
      console.error('verifySignInCode failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setVerifying(false);
    }
  }

  // STATE 2 link: re-request a code for the same email.
  async function handleResend() {
    const value = email.trim().toLowerCase();
    if (!value) return;
    setError('');
    setNotice('');
    try {
      const fn = httpsCallable(functions, 'sendSignInCode');
      await fn({ email: value });
      setNotice('A new code is on its way.');
    } catch (err) {
      console.error('sendSignInCode (resend) failed:', err);
      setError("We couldn't resend your code. Please wait a moment and try again.");
    }
  }

  // STATE 2 link: return to email entry to use a different address.
  function handleUseDifferentEmail() {
    setStep('email');
    setCode('');
    setError('');
    setNotice('');
  }

  if (step === 'email') {
    return (
      <div className="space-y-6">
        {emailIntro && (
          <p className="text-foreground text-center text-lg">
            {emailIntro}
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="signin-email" className="text-foreground">
            Email
          </Label>
          <Input
            id="signin-email"
            type="email"
            inputMode="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-12 text-base"
          />
        </div>

        {error && (
          <p className="text-destructive text-sm text-center">{error}</p>
        )}

        <Button
          onClick={handleEmailSubmit}
          disabled={sending}
          className="w-full h-12 text-base"
        >
          {sending ? 'Sending...' : 'Email me a code'}
        </Button>

        <p className="text-muted-foreground text-sm text-center">
          We'll send a 6-digit code to sign you in. No password needed.
        </p>
      </div>
    );
  }

  // step === 'code'
  return (
    <div className="space-y-6">
      <p className="text-foreground text-center text-lg">
        We've sent a code to {email}.
      </p>

      <div className="space-y-2">
        <Label htmlFor="signin-code" className="text-foreground">
          6-digit code
        </Label>
        <Input
          id="signin-code"
          inputMode="numeric"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="h-12 text-center text-2xl tracking-[0.5em]"
        />
      </div>

      {error && (
        <p className="text-destructive text-sm text-center">{error}</p>
      )}
      {notice && (
        <p className="text-muted-foreground text-sm text-center">{notice}</p>
      )}

      <Button
        onClick={handleSignIn}
        disabled={verifying}
        className="w-full h-12 text-base"
      >
        {verifying ? 'Signing in...' : 'Sign in'}
      </Button>

      <div className="flex flex-col items-center gap-3">
        <button
          type="button"
          onClick={handleResend}
          className="text-primary text-sm underline-offset-4 hover:underline"
        >
          Resend code
        </button>
        <button
          type="button"
          onClick={handleUseDifferentEmail}
          className="text-muted-foreground text-sm underline-offset-4 hover:underline"
        >
          Use a different email
        </button>
      </div>

      <p className="text-muted-foreground text-sm text-center">
        This code expires in 15 minutes.
      </p>
    </div>
  );
}
